// Disabled until we find out how to define prop types in this case
/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'Store';
// eslint-disable-next-line import/no-unresolved
import { isArchive, isLiveTv, isEpisode } from 'Utils';
// TODO: Resolve this in TS/ESLINT config..
// eslint-disable-next-line import/no-extraneous-dependencies
import { BINGE_WATCHING_VOD_DURATION, BINGE_WATCHING_LOADING_DURATION } from 'Options';
import { SeasonModel } from '@24i/st-api-connector';
import type { onModalHiddenParams, PlayerData } from 'Types';
import { Device } from 'DeviceUtils';
import { findNextEpisode } from '../../PlayerUI/components/BingeWatchingModal/getNextEpisode';
import { playerModalsEnum } from '../../PlayerUI/components/PlayerModal/PlayerModal';

export const getSortedEpisodes = (season: SeasonModel) =>
    season
        .getEpisodes()
        .sort(
            (episode, nextEpisode) =>
                (episode.getRawData().episodeNumber || 0) -
                (nextEpisode.getRawData().episodeNumber || 0)
        );

const useBingeWatching = (
    item: PlayerData['item'] | null,
    changeItem,
    onPlayerProgress,
    modalType: playerModalsEnum | null,
    setModalType: (type: playerModalsEnum | null) => void,
    currentAssetTime: number | null,
    liveLoading: boolean,
    isLiveVOD: boolean,
    playerInstance: any,
    player: any
) => {
    const { appManager } = useStore();
    const nextItem = useRef({});
    const [nextEpisode, setNextEpisode] = useState<PlayerData['item'] | null>(null);
    const [nextProgram, setNextProgram] = useState<PlayerData['item'] | null>(null);
    const getMetaData = item?.getMetaData();
    const metaDataDetail = getMetaData && Object.values(getMetaData)[1];
    const nextBingeItem = useMemo(() => {
        if (isArchive(item) || isLiveVOD || isLiveTv(item)) {
            return nextProgram;
        }
        return nextEpisode;
    }, [nextProgram, nextEpisode, isLiveVOD]);
    const updateBingeItem = useCallback(() => {
        if (isArchive(item) || isLiveVOD || isLiveTv(item)) {
            getNextProgram();
            return;
        }
        getNextEpisode();
    }, [item, isLiveVOD]);

    // eslint-disable-next-line max-statements
    const getNextEpisode = async () => {
        if (isLiveTv(item)) return;
        try {
            const series = await appManager.getApi().fetchSeries(metaDataDetail?.serieId);
            const nextEp = findNextEpisode(series, item);
            const currentEpisodeSeason = metaDataDetail?.seasonNumber;
            const nextSeason = series?.seasons.find(
                (season) => season.season.seasonNumber === currentEpisodeSeason + 1
            );

            let currentSeason;
            if (series?.seasons.length === 1) {
                currentSeason = series?.seasons[0];
            } else {
                const findCurrentSeason = series?.seasons.find(
                    (season) => season.season.id === metaDataDetail?.serieSeasonId
                );
                currentSeason = findCurrentSeason;
            }

            const lastEpisodeId = currentSeason.episodes.slice(-1)[0].data.id;

            if (metaDataDetail?.id === lastEpisodeId) {
                if (!nextSeason) {
                    setNextEpisode(null);
                    return;
                }
                const firstEpisode = nextSeason.episodes[0];
                setNextEpisode(firstEpisode);
                return;
            }
            setNextEpisode(nextEp);
        } catch (error) {
            console.log('COULD NOT FETCH NEXT EPISODE: ', error);
        }
    };

    const getNextProgram = async () => {
        try {
            const showtimes = await appManager.getApi().fetchShowTimes({
                channelId: item.getChannelId(),
                endTime: metaDataDetail?.start
            });
            const nextArchiveProgram = showtimes.find(
                (program) => program.getStartTime() >= metaDataDetail?.end
            );
            setNextProgram(nextArchiveProgram);
        } catch (error) {
            console.log('COULD NOT FETCH NEXT PROGRAM: ', error);
        }
    };

    useEffect(() => {
        if (isLiveTv(item) || isLiveVOD) {
            getNextProgram();
            return;
        }
        getNextEpisode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    // maybe usefull
    // const showBingeWatchingModal = () => {
    //     setModalType(playerModalsEnum.bingeWatching);
    // };

    //     useEffect(() => {
    //         if (currentAssetTime && !isBingeLoadingRef.current) {
    //             const currentTimeSeconds =
    //                 currentAssetTime >= metaDataDetail.start
    //                     ? Math.round((currentAssetTime - metaDataDetail.start) / 1000)
    //                     : Math.round(currentAssetTime / 1000);
    //             const episodeBingeTime = metaDataDetail.duration - 6;
    //             const currentProgramDuration = (metaDataDetail.end - metaDataDetail.start) / 1000;
    //             const programBingeTime = currentProgramDuration - 6;
    //             const isNearEnd =
    //                 currentTimeSeconds >= episodeBingeTime || currentTimeSeconds >= programBingeTime;
    //             const hasNextAsset = nextEpisode !== null || nextProgram !== null;
    // ,                        episodeBingeTime: ${episodeBingeTime},
    //                          currentProgramDuration: ${currentProgramDuration},
    //                          programBingeTime: ${programBingeTime},
    //                          isNearEnd: ${isNearEnd},
    //                          hasNextAsset: ${hasNextAsset}`);
    //             if (
    //                 hasNextAsset &&
    //                 isNearEnd &&
    //                 modalType !== playerModalsEnum.bingeWatching &&
    //                 !liveLoading
    //             ) {
    //                 startBingeTimeout();
    //                 showBingeWatchingModal();
    //             }
    //         }

    //         // eslint-disable-next-line react-hooks/exhaustive-deps
    //     }, [currentAssetTime]);

    const onModalHidden = (params?: onModalHiddenParams) => {
        if (params?.openTvGuide) {
            setModalType(playerModalsEnum.miniEpg);
            return;
        }
        if (params?.initNextItem) changeItem(nextItem.current, true);
        setModalType(null);
    };

    const onProgress = (progressDataObject) => {
        const {
            duration: durationIn,
            progressData: { currentTime }
        } = progressDataObject;

        if (Device.isWeb && player.live) {
            return;
        }

        const duration =
            Device.isWeb &&
            player?.mediaPlayer?.getDashMetrics()?.getCurrentDVRInfo('video')?.range?.end !==
                undefined &&
            player?.mediaPlayer?.getDashMetrics()?.getCurrentDVRInfo('video')?.range?.start !==
                undefined
                ? (player.mediaPlayer.getDashMetrics().getCurrentDVRInfo('video').range.end -
                      player.mediaPlayer.getDashMetrics().getCurrentDVRInfo('video').range.start) *
                  1000
                : durationIn;

        if (!isEpisode(item) && (duration === 0 || currentTime === 0)) return;

        if (typeof onPlayerProgress === 'function') onPlayerProgress(progressDataObject);

        const bingeCondition = isEpisode(item)
            ? // @ts-ignore
              currentTime / 1000 > item.getDuration() - BINGE_WATCHING_VOD_DURATION
            : currentTime >= duration - BINGE_WATCHING_LOADING_DURATION;
        if (
            bingeCondition
            // Math.floor(duration - currentTime) <=
            // BINGE_WATCHING_END_SECONDS_LIMIT
        ) {
            // Do not override possibly open tv guide modal
            if (
                !liveLoading &&
                (nextEpisode ||
                    (nextProgram &&
                        modalType !== playerModalsEnum.bingeWatching &&
                        modalType !== playerModalsEnum.miniEpg))
            ) {
                setModalType(playerModalsEnum.bingeWatching);
            }
        }
    };

    return {
        onModalHidden,
        onProgress,
        nextBingeItem,
        updateBingeItem
    };
};

export default useBingeWatching;
